import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { AttachMoney, EventAvailable, TrendingUp } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { reportApi } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import { ReportDateRangeFilter } from '../../components/reports/ReportDateRangeFilter';
import { ReportPieChart } from '../../components/reports/ReportPieChart';
import { useReportDateRange } from '../../hooks/useReportDateRange';
import { getQueryErrorMessage } from '../../utils/queryError';

export const RevenueReportPage: React.FC = () => {
  const { t } = useTranslation();
  const { range, setRange, minimumStartDate } = useReportDateRange();

  const { data: report, error, isError, isLoading } = useQuery(
    ['revenueReport', range.startDate, range.endDate],
    () => reportApi.getRevenue(range.startDate, range.endDate)
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('reports.revenue.title', 'Revenue Report')}
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        {t('reports.revenue.subtitle', 'Track completed-rental revenue over time and by vehicle class')}
      </Typography>

      <ReportDateRangeFilter
        value={range}
        onChange={setRange}
        minimumStartDate={minimumStartDate}
      />

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Alert severity="error">
          {getQueryErrorMessage(
            error,
            t('reports.revenue.loadError', 'Unable to load the revenue report for that date range.')
          )}
        </Alert>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TrendingUp color="success" />
                    <Typography variant="body2" color="text.secondary">
                      {t('reports.revenue.total', 'Total Revenue')}
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="success.main">
                    {formatCurrency(report?.totalRevenue)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <EventAvailable color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      {t('reports.revenue.completedReservations', 'Completed Reservations')}
                    </Typography>
                  </Box>
                  <Typography variant="h4">{report?.completedReservationCount ?? 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AttachMoney color="info" />
                    <Typography variant="body2" color="text.secondary">
                      {t('reports.revenue.averageValue', 'Average Reservation Value')}
                    </Typography>
                  </Box>
                  <Typography variant="h4">{formatCurrency(report?.averageReservationValue)}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('reports.revenue.trend', 'Monthly Revenue Trend')}
                  </Typography>
                  {!report || report.trend.length === 0 ? (
                    <Alert severity="info">{t('reports.revenue.empty', 'No completed reservations in this period')}</Alert>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('reports.revenue.period', 'Period')}</TableCell>
                            <TableCell align="right">{t('reports.revenue.total', 'Total Revenue')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.trend.map((point) => (
                            <TableRow key={`${point.year}-${point.month}`}>
                              <TableCell>{point.label}</TableCell>
                              <TableCell align="right">{formatCurrency(point.revenue)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('reports.revenue.byVehicleClass', 'Revenue by Vehicle Class')}
                  </Typography>
                  <ReportPieChart
                    data={(report?.byVehicleClass ?? []).map((slice) => ({ name: slice.label, value: slice.amount }))}
                    emptyLabel={t('reports.revenue.empty', 'No completed reservations in this period')}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};
